import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, MagnifyingGlass } from "@phosphor-icons/react";

const emptyForm = { nome: "", cognome: "", email: "", telefono: "", indirizzo: "", data_nascita: "", preferenze: "", allergie: "", note: "" };

export default function Clienti() {
  const [clienti, setClienti] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/clienti").then((r) => setClienti(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm(Object.fromEntries(Object.keys(emptyForm).map((k) => [k, c[k] || ""])));
    setOpen(true);
  };

  const save = async () => {
    if (!form.nome.trim()) { toast.error("Il nome è obbligatorio"); return; }
    try {
      if (editing) await api.put(`/clienti/${editing.id}`, form);
      else await api.post("/clienti", form);
      toast.success(editing ? "Scheda cliente aggiornata" : "Cliente creato");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare il cliente "${c.nome} ${c.cognome}"?`)) return;
    try {
      await api.delete(`/clienti/${c.id}`);
      toast.success("Cliente eliminato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? clienti.filter((c) => `${c.nome} ${c.cognome} ${c.email} ${c.telefono}`.toLowerCase().includes(q))
    : clienti;

  const field = (label, key, props = {}) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} data-testid={`cliente-${key}-input`} {...props} />
    </div>
  );

  return (
    <div className="space-y-6 fade-in" data-testid="clienti-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold">Clienti</h1>
          <p className="text-[#6B7280] mt-1">Anagrafica clienti: contatti, preferenze alimentari e allergie.</p>
        </div>
        <Button onClick={openNew} data-testid="cliente-add-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F] gap-2">
          <Plus size={18} weight="bold" /> Nuovo cliente
        </Button>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome, email o telefono..."
          data-testid="clienti-search-input"
          className="pl-10 rounded-full bg-white"
        />
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
        <Table data-testid="clienti-table">
          <TableHeader className="bg-[#FAF9F6]">
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Contatti</TableHead>
              <TableHead>Data di nascita</TableHead>
              <TableHead>Allergie</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-[#6B7280] py-10" data-testid="clienti-empty">
                  {clienti.length === 0 ? "Nessun cliente registrato. Crea la prima scheda cliente." : "Nessun cliente corrisponde alla ricerca."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((c) => (
              <TableRow key={c.id} className="hover:bg-stone-50" data-testid={`cliente-row-${c.id}`}>
                <TableCell>
                  <p className="font-semibold">{c.nome} {c.cognome}</p>
                  {c.indirizzo && <p className="text-xs text-[#6B7280]">{c.indirizzo}</p>}
                </TableCell>
                <TableCell>
                  <p className="text-sm">{c.email || "—"}</p>
                  <p className="text-xs text-[#6B7280]">{c.telefono}</p>
                </TableCell>
                <TableCell>{c.data_nascita ? new Date(c.data_nascita).toLocaleDateString("it-IT") : "—"}</TableCell>
                <TableCell>
                  {c.allergie ? (
                    <Badge className="rounded-full bg-red-50 text-[#D32F2F] hover:bg-red-50">{c.allergie}</Badge>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`cliente-edit-${c.id}`} className="hover:bg-stone-100">
                    <PencilSimple size={18} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`cliente-delete-${c.id}`} className="hover:bg-red-50 text-[#D32F2F]">
                    <Trash size={18} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cliente-dialog">
          <DialogHeader><DialogTitle>{editing ? "Scheda cliente" : "Nuova scheda cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("Nome *", "nome")}
              {field("Cognome", "cognome")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("Email", "email", { type: "email" })}
              {field("Telefono", "telefono")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("Indirizzo", "indirizzo")}
              {field("Data di nascita", "data_nascita", { type: "date" })}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("Preferenze alimentari", "preferenze", { placeholder: "es. vegetariano, no piccante" })}
              {field("Allergie", "allergie", { placeholder: "es. crostacei, glutine" })}
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="cliente-note-input" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annulla</Button>
            <Button onClick={save} data-testid="cliente-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">
              {editing ? "Salva modifiche" : "Crea cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
